import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
/** Trophy art: `frontend/src/img/trophy.png` (project asset — not the full-page mock reference). */
import trophyImg from "../../img/trophy.png";
import { fetchLeaderboard } from "../../functions/fetchLeaderboard";
import { setBackButtonUrl } from "../uiSlice";
import "./leaderboard.css";

const SPARKLES = [
  { top: "8%", left: "6%", delay: "0s", size: 8 },
  { top: "15%", left: "88%", delay: "1s", size: 6 },
  { top: "35%", left: "92%", delay: "0.5s", size: 5 },
  { top: "60%", left: "4%", delay: "1.5s", size: 7 },
  { top: "75%", left: "82%", delay: "0.8s", size: 5 },
  { top: "90%", left: "18%", delay: "0.3s", size: 6 },
];

/** Resolve elapsed from API (`play_duration` preferred; legacy `time`; or seconds). */
function rawLeaderDuration(leader) {
  if (leader == null || typeof leader !== "object") return "";
  const v =
    leader.play_duration ??
    leader.playDuration ??
    leader.time ??
    leader.TIME ??
    leader.elapsed ??
    leader.time_sec ??
    leader.timeSec;
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(Math.max(0, Math.floor(v)));
  return String(v).trim();
}

/** `stages.time` / `play_duration`: HH:MM:SS or MM:SS, or plain seconds; display as MM:SS. */
function formatLeaderTime(raw) {
  if (raw == null || raw === "") return "—";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const sec = Math.max(0, Math.floor(raw));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  const s = String(raw).trim();
  if (!s) return "—";
  if (/^\d+$/.test(s)) {
    return formatLeaderTime(Number.parseInt(s, 10));
  }
  const segments = s.split(":").map((p) => String(p).replace(/\D/g, ""));
  if (segments.length >= 2) {
    const mm = segments[segments.length - 2] || "0";
    const ss = segments[segments.length - 1] || "0";
    const mNum = Number.parseInt(mm, 10);
    const sNum = Number.parseInt(ss, 10);
    const mSafe = Number.isFinite(mNum) ? mNum : 0;
    const sSafe = Number.isFinite(sNum) ? sNum : 0;
    return `${String(mSafe).padStart(2, "0")}:${String(sSafe).padStart(2, "0")}`;
  }
  return s;
}

/** Sort leaderboard rows: highest score first, then fastest time. */
function sortLeaders(list) {
  return [...list].sort((a, b) => {
    const scoreDiff = (Number(b.total_score) || 0) - (Number(a.total_score) || 0);
    if (scoreDiff !== 0) return scoreDiff;
    return String(rawLeaderDuration(a) || "").localeCompare(String(rawLeaderDuration(b) || ""));
  });
}

function renderLeaderRow(leader, rank, { isMe, key }) {
  const name = leader.name || leader.userid || "—";
  const timeDisp = formatLeaderTime(rawLeaderDuration(leader));
  const pts = Number(leader.total_score) || 0;
  const ptsDisp = String(pts).padStart(2, "0");
  const rankClass =
    rank === 1
      ? "leaderboard-rank leaderboard-rank--1"
      : rank === 2
        ? "leaderboard-rank leaderboard-rank--2"
        : rank === 3
          ? "leaderboard-rank leaderboard-rank--3"
          : "leaderboard-rank";

  return (
    <tr key={key} className={isMe ? "leaderboard-row-me" : undefined}>
      <td className={rankClass}>{rank}</td>
      <td>
        {name}
        {isMe ? <span className="you-tag"> (You)</span> : null}
      </td>
      <td>{timeDisp}</td>
      <td>{ptsDisp}</td>
    </tr>
  );
}

const TOP_N = 5;

export default function Leaderboard() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [allLeaders, setAllLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(setBackButtonUrl("/leaderboard"));
  }, [dispatch]);

  useEffect(() => {
    const push = () => {
      try {
        window.history.pushState(null, "", window.location.href);
      } catch {
        /* ignore */
      }
    };
    push();
    const onPopState = (e) => {
      e?.preventDefault?.();
      push();
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const storedUserData = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData") || "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);

  const currentUserId = useMemo(
    () =>
      user?.userId ??
      user?.userid ??
      user?.id ??
      storedUserData.userId ??
      storedUserData.userid ??
      storedUserData.id,
    [user, storedUserData]
  );

  const sessionId = location.state?.sessionId || storedUserData.sessionId;
  const organizationId = location.state?.organizationId || storedUserData.organizationId;
  const token = sessionId && organizationId ? `${sessionId}&${organizationId}` : "";

  useEffect(() => {
    if (!token || !sessionId || !organizationId) {
      setError("Missing session data");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLeaderboard({ token, sessionId, organizationId });
        if (cancelled) return;
        const sorted = Array.isArray(data) ? sortLeaders(data) : [];
        setAllLeaders(sorted);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, sessionId, organizationId]);

  const isCurrentUser = (userId) => String(userId) === String(currentUserId);

  const topFive = useMemo(() => allLeaders.slice(0, TOP_N), [allLeaders]);

  const myLeaderEntry = useMemo(() => {
    if (!currentUserId) return null;
    const idx = allLeaders.findIndex((leader) => isCurrentUser(leader.userid));
    if (idx < 0) return null;
    return { leader: allLeaders[idx], rank: idx + 1 };
  }, [allLeaders, currentUserId]);

  const showMeBelowTop = myLeaderEntry != null && myLeaderEntry.rank > TOP_N;

  return (
    <div className="leaderboard-page">
      {SPARKLES.map((s, i) => (
        <div
          key={i}
          className="sparkle"
          style={{
            top: s.top,
            left: s.left,
            width: s.size,
            height: s.size,
            animationDelay: s.delay,
          }}
        />
      ))}

      <header className="leaderboard-title-row">
        <img src={trophyImg} alt="" className="leaderboard-trophy" width={64} height={64} decoding="async" />
        <h1 className="leaderboard-title-main">LEADERBOARD</h1>
        <img src={trophyImg} alt="" className="leaderboard-trophy" width={64} height={64} decoding="async" />
      </header>

      {loading ? (
        <div className="leaderboard-loading">Loading leaderboard…</div>
      ) : error ? (
        <div className="leaderboard-error">{error}</div>
      ) : allLeaders.length === 0 ? (
        <div className="leaderboard-empty">No leaderboard data available</div>
      ) : (
        <div className="leaderboard-panel">
          <table className="leaderboard-table">
            <colgroup>
              <col className="leaderboard-col-rank" />
              <col className="leaderboard-col-name" />
              <col className="leaderboard-col-time" />
              <col className="leaderboard-col-points" />
            </colgroup>
            <thead>
              <tr className="leaderboard-head-row">
                <th scope="col" className="leaderboard-th-rank">
                  RANK
                </th>
                <th scope="col" className="leaderboard-th-name">
                  NAME
                </th>
                <th scope="col" className="leaderboard-th-time">
                  TIME
                </th>
                <th scope="col" className="leaderboard-th-points">
                  POINTS
                </th>
              </tr>
            </thead>
            <tbody>
              {topFive.map((leader, index) =>
                renderLeaderRow(leader, index + 1, {
                  isMe: isCurrentUser(leader.userid),
                  key: String(leader.userid ?? leader.email ?? leader.name ?? index),
                })
              )}
              {showMeBelowTop && myLeaderEntry
                ? renderLeaderRow(myLeaderEntry.leader, myLeaderEntry.rank, {
                    isMe: true,
                    key: `me-${myLeaderEntry.rank}`,
                  })
                : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
